import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * VoiceRecorder
 * - Click "Record" to start recording from the user's microphone
 * - Click "Stop" to finish recording
 * - Shows an <audio controls> player to play back the captured audio
 * - The ref exposes toggleRecording / playRecording for the Speak it dialog's hotkeys
 */
function VoiceRecorder(_props, ref) {
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [durationMs, setDurationMs] = useState(0);

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const startTimeRef = useRef(null);
  const mountedRef = useRef(true);
  const startingRef = useRef(false);
  const audioRef = useRef(null);

  function chooseMimeType() {
    if (typeof window === "undefined" || typeof MediaRecorder === "undefined") return "";
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a",
      "audio/mp4",
      "audio/ogg;codecs=opus",
      "audio/ogg"
    ];
    for (const c of candidates) {
      if (MediaRecorder.isTypeSupported?.(c)) return c;
    }
    return "";
  }

  useEffect(() => {
    setIsSupported(typeof window !== "undefined" && typeof MediaRecorder !== "undefined" && navigator?.mediaDevices?.getUserMedia !== undefined);
  }, []);

  // A new recording replaces the previous one, so release the old blob URL.
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  // Closing the modal mid-recording unmounts this: release the microphone and the timer,
  // and keep the recorder's late onstop from setting state on an unmounted component.
  // A recording still waiting on the mic permission prompt is caught by mountedRef in
  // startRecording, since its stream and recorder don't exist yet for this to stop.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(tickRef.current);
      const mr = mediaRecorderRef.current;
      if (mr) {
        mr.onstop = null;
        mr.ondataavailable = null;
        if (mr.state !== "inactive") mr.stop();
      }
      stopTracks();
    };
  }, []);

  function stopTracks() {
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  }

  async function startRecording() {
    // a second click while the permission prompt is up (or already recording) would start
    // a second recorder and orphan the first stream
    if (startingRef.current || mediaRecorderRef.current?.state === "recording") return;
    startingRef.current = true;
    setIsStarting(true);
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current) {
        // closed (or moved on to the next sentence) before permission was granted
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      mediaStreamRef.current = stream;
      const mimeType = chooseMimeType();
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || undefined });
        setAudioUrl(URL.createObjectURL(blob));
        stopTracks();
        setIsRecording(false);
        clearInterval(tickRef.current);
      };

      mr.start();
      setIsRecording(true);
      setDurationMs(0);
      startTimeRef.current = performance.now();
      // the display only shows whole seconds
      tickRef.current = setInterval(() => setDurationMs(performance.now() - startTimeRef.current), 250);
    } catch (err) {
      stopTracks();
      if (!mountedRef.current) return;
      console.error(err);
      setError(err && err.name === "NotAllowedError" ?
        "Microphone access was blocked. Allow it in your browser settings to record." :
        "Failed to start recording.");
    } finally {
      startingRef.current = false;
      if (mountedRef.current) setIsStarting(false);
    }
  }

  function stopRecording() {
    try {
      mediaRecorderRef.current?.stop();
    } catch (err) {
      console.error(err);
      setError("Couldn't stop recording.");
    }
  }

  useImperativeHandle(ref, () => ({
    playRecording: () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.currentTime = 0;
      audio.play()?.catch?.(() => {});
    },
    toggleRecording: () => {
      if (mediaRecorderRef.current?.state === "recording") {
        stopRecording();
      } else if (isSupported) {
        startRecording();
      }
    }
  }));

  function formatDuration(ms) {
    const sec = Math.floor(ms / 1000);
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }

  return (
    <div>
      {!isSupported && (
        <p className="alert alert-danger">
          Your browser doesn&apos;t support recording (MediaRecorder/getUserMedia not available).
        </p>
      )}

      {error && (
        <p className="alert alert-danger">{error}</p>
      )}

      <div style={{ marginBottom: 10 }}>
        {!isRecording ? (
          <button
            className="btn btn-default"
            disabled={!isSupported || isStarting}
            onClick={startRecording}
            type="button"
          >
            Record
          </button>
        ) : (
          <button
            className="btn btn-default"
            onClick={stopRecording}
            type="button"
          >
            Stop
          </button>
        )}

        <span style={{ paddingLeft: 10 }}>
          {formatDuration(durationMs)}
        </span>
      </div>

      {audioUrl && (
        <audio autoPlay={true} controls ref={audioRef} src={audioUrl} />
      )}
    </div>
  );
}

export default forwardRef(VoiceRecorder);
